import type { Snack } from "@kaleidoscope-solutions/ks-design-system"
import type { MaybeRefOrGetter } from "vue"
import { until } from "@vueuse/core"
import { computed, readonly, ref } from "vue"

type SnackType = `${Snack}`
interface Action {
  label: string
  action: () => void
}

export interface Snackbar {
  type: SnackType
  message: string
  // Progress bar would be nice to add at some point
  // As would a 'only close on click' option
  timeout?: number
  actions?: Action[]
  refOrGetter?: MaybeRefOrGetter
  condition?: (v: MaybeRefOrGetter) => boolean
}

const snacks = ref<Snackbar[]>([])

export function useSnackbar() {
  const removeSnackbar = (snack: Snackbar) => {
    const index = snacks.value.findIndex((snackItem: Snackbar) => snackItem.message === snack.message)
    if (index > -1) {
      snacks.value.splice(index, 1)
    }
  }

  const addSnackbar = async (snack: Snackbar) => {
    const timeout = snack.timeout ?? 30000

    snacks.value.push(snack)

    if (snack.refOrGetter && snack.condition) {
      const { refOrGetter, condition } = snack
      await until(refOrGetter).toMatch((v) => {
        return condition(v)
      }, { timeout })
      removeSnackbar(snack)
    } else {
      setTimeout(() => removeSnackbar(snack), timeout)
    }
  }

  const hasSnacks = computed(() => snacks.value.length > 0)

  return {
    snacks: readonly(snacks.value),
    removeSnackbar,
    addSnackbar,
    hasSnacks,
  }
}
